import type { ComponentsUnionType, ComputedBlockStateUnionType } from '@unifyapps/defs/types/block';
import { isNative } from '@unifyapps/ui/utils/isNative';
import { useMemo } from 'react';
import type { BlockRendererProps } from '../context/RegistryProvider/types';
import { BlockDataAttributes, useBlockDataAttributes } from './useBlockDataAttributes';
import useGetBlockBehavior from './useGetBlockBehavior';

interface UseBlockAttributesAndEventListenersProps {
  id: string;
  component: ComponentsUnionType;
  pageId: string;
  blockStateId: string;
  computedBlockState: ComputedBlockStateUnionType;
  propsDataAttributes?: Record<string, unknown>;
  dndParams?: BlockRendererProps['dndParams'];
  blockVersion?: number;
  // True only on the slot-root Stack block (driven by `block.additional.isSlotRoot`).
  // Emits `data-slot-root` so the canvas hover/click handler can recognize the
  // slot subtree boundary and bypass the enclosing TC's module-wrapper resolution.
  isSlotRoot?: boolean;
}

export function useBlockAttributesAndEventListeners({
  id,
  component,
  pageId,
  blockStateId,
  computedBlockState,
  propsDataAttributes,
  dndParams,
  blockVersion,
  isSlotRoot,
}: UseBlockAttributesAndEventListenersProps) {
  const detachFromLayout = Boolean(useGetBlockBehavior(component.componentType)?.detachFromLayout);
  const dataAttributes = useBlockDataAttributes({
    id,
    component,
    pageId,
    blockStateId,
    blockVersion,
    detachFromLayout,
  });

  const attributesAndEventListeners = useMemo(() => {
    if (isNative()) return dataAttributes;
    const htmlAttributesObj = computedBlockState.additional?.htmlAttributes?.reduce(
      (acc, { key, value }) => {
        acc[key] = value;
        return acc;
      },
      {},
    );

    return {
      ...dataAttributes,
      ...htmlAttributesObj,
      ...propsDataAttributes,
      ...dndParams?.attributes,
      ...dndParams?.listenersRef.current,
      ...(isSlotRoot ? { [BlockDataAttributes.SlotRoot]: 'true' } : {}),
    } as Record<string, unknown>;
  }, [
    dataAttributes,
    dndParams?.attributes,
    dndParams?.listenersRef,
    propsDataAttributes,
    computedBlockState.additional?.htmlAttributes,
    isSlotRoot,
  ]);

  return attributesAndEventListeners;
}
